import React, { useState, useMemo } from 'react';
import { X, Calendar, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import StripePaymentForm from './StripePaymentForm';
import ModalShell from '../shared/ModalShell';
import {
 resolvePaymentTerms,
 calculateRemainingPayments,
 rulesForPaymentCalculation,
} from '../../utils/paymentTerms';

const BalancePaymentModal = ({
 isOpen,
 onClose,
 booking,
 balanceAmount,
 payments = [],
 onPaymentSuccess,
 onPaymentError
}) => {
 const [cardholderName, setCardholderName] = useState('');
 const [selectedPayment, setSelectedPayment] = useState(null);
 const [showPaymentSchedule, setShowPaymentSchedule] = useState(true);
 const [payAllRemaining, setPayAllRemaining] = useState(true);

 // Calculate payment terms based on booking event date
 const paymentTerms = useMemo(() => {
 if (!booking?.event_date) return null;
 const subtotal = parseFloat(booking?.subtotal_amount || 0);
 const platformFee = parseFloat(booking?.platform_fee || 0);
 const rules = rulesForPaymentCalculation(
 booking?.vendor_service_package?.payment_schedule_rules ??
 booking?.package?.payment_schedule_rules
 );
 return resolvePaymentTerms(booking.event_date, subtotal, rules, { platformFee });
 }, [
 booking?.event_date,
 booking?.subtotal_amount,
 booking?.platform_fee,
 booking?.vendor_service_package?.payment_schedule_rules,
 booking?.package?.payment_schedule_rules,
 ]);

 // Get completed payments
 const completedPayments = useMemo(() => {
 return payments.filter(p => p.status === 'completed') || [];
 }, [payments]);

 // Remaining installments only — deposit was already paid at booking
 const remainingPayments = useMemo(() => {
 if (!paymentTerms || !booking) return [];
 const subtotal = parseFloat(booking.subtotal_amount || 0);
 const platformFee = parseFloat(booking.platform_fee || 0);
 const allRemaining = calculateRemainingPayments(
 paymentTerms,
 completedPayments,
 subtotal,
 platformFee
 );

 return allRemaining.filter((payment) => {
 const isDepositInstallment =
 payment.index === 0 || /deposit/i.test(payment.label || '');

 if (isDepositInstallment) return false;
 if ((payment.remainingAmount || 0) <= 0.01) return false;

 return true;
 });
 }, [paymentTerms, completedPayments, booking]);

 React.useEffect(() => {
 if (!isOpen) return;

 if (remainingPayments.length === 0) {
 setPayAllRemaining(true);
 setSelectedPayment(null);
 return;
 }

 if (!selectedPayment && !payAllRemaining) {
 setSelectedPayment(remainingPayments[0]);
 }
 }, [isOpen, remainingPayments, selectedPayment, payAllRemaining]);

 // Calculate selected payment amount
 const selectedPaymentAmount = useMemo(() => {
 if (payAllRemaining) {
 return balanceAmount; // Pay all remaining balance
 }
 if (!selectedPayment || !booking) return balanceAmount;
 return selectedPayment.remainingAmount || selectedPayment.amount;
 }, [selectedPayment, booking, balanceAmount, payAllRemaining]);

 const paymentType = useMemo(() => 'full_payment', []);

 // Memoize metadata - cardholderName can be included because metadata changes
 // won't trigger payment intent recreation (metadata is not in useEffect deps)
 const paymentMetadata = useMemo(() => ({
 cardholderName: cardholderName,
 onCardholderNameChange: setCardholderName,
 paymentLabel: payAllRemaining 
 ? 'Pay All Remaining Balance' 
 : (selectedPayment?.label || 'Balance Payment'),
 paymentPercentage: payAllRemaining 
 ? (() => {
 const totalDue = parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0);
 return totalDue > 0 ? (balanceAmount / totalDue) * 100 : null;
 })()
 : (selectedPayment?.percentage || null),
 // Send proportional platform fee information for backend calculation
 paymentPlatformFee: payAllRemaining
 ? (() => {
 const totalDue = parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0);
 const remainingPercentage = totalDue > 0 ? (balanceAmount / totalDue) * 100 : 0;
 return (parseFloat(booking?.platform_fee || 0) * remainingPercentage) / 100;
 })()
 : (selectedPayment 
 ? (parseFloat(booking?.platform_fee || 0) * selectedPayment.percentage) / 100
 : null),
 totalPlatformFee: parseFloat(booking?.platform_fee || 0)
 }), [
 cardholderName,
 payAllRemaining,
 selectedPayment,
 balanceAmount,
 booking?.subtotal_amount,
 booking?.platform_fee
 ]);

 const open = Boolean(isOpen && booking && balanceAmount > 0);

 return (
 <ModalShell
 isOpen={open}
 onClose={onClose}
 labelledBy="balance-payment-title"
 panelClassName="max-w-md"
 >
 {booking && balanceAmount > 0 && (
 <div className="bg-surface rounded-lg border border-border shadow-sm p-4 sm:p-6 relative overflow-hidden">
 <button
 type="button"
 onClick={onClose}
 className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors p-1 rounded-md hover:bg-surface-muted z-10"
 aria-label="Close"
 >
 <X className="w-5 h-5" />
 </button>

 <div className="mb-6 pr-8">
 <h2
 id="balance-payment-title"
 className="text-xl font-bold text-ink mb-2"
 >
 Pay Remaining Balance
 </h2>
 <p className="text-sm text-ink-secondary">
 Complete your payment for booking #{booking.booking_reference}
 </p>
 </div>

 <div className="bg-surface-muted border border-border rounded-xl p-4 mb-6">
 <div className="flex justify-between items-center mb-2">
 <span className="text-sm text-ink-secondary">Subtotal:</span>
 <span className="text-sm font-medium text-ink">${parseFloat(booking?.subtotal_amount || 0).toFixed(2)}</span>
 </div>
 <div className="flex justify-between items-center mb-2">
 <span className="text-sm text-ink-secondary">Service Charge</span>
 <span className="text-sm font-medium text-ink">${parseFloat(booking?.platform_fee || 0).toFixed(2)}</span>
 </div>
 <div className="flex justify-between items-center mb-2">
 <span className="text-sm text-ink-secondary">Total Amount:</span>
 <span className="text-sm font-medium text-ink">
 ${(parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0)).toFixed(2)}
 </span>
 </div>
 <div className="flex justify-between items-center mb-2">
 <span className="text-sm text-ink-secondary">Amount Paid:</span>
 <span className="text-sm font-medium text-ink">-${parseFloat(booking?.amount_paid || 0).toFixed(2)}</span>
 </div>
 <div className="flex justify-between items-center pt-2 border-t border-gray-300">
 <span className="text-base font-bold text-ink">Balance Due:</span>
 <span className="text-lg font-bold text-ink">${balanceAmount.toFixed(2)}</span>
 </div>
 </div>

 {paymentTerms && balanceAmount > 0 && (
 <div className="border-2 border-border rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 bg-surface-muted">
 <button
 type="button"
 onClick={() => setShowPaymentSchedule(!showPaymentSchedule)}
 className="w-full flex items-center justify-between text-left mb-3 sm:mb-4"
 >
 <div className="flex items-center">
 <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-ink mr-2 sm:mr-3" />
 <h3 className="font-bold text-base sm:text-lg text-ink">
 Choose Payment Option
 </h3>
 </div>
 {showPaymentSchedule ? (
 <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-ink-secondary" />
 ) : (
 <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-ink-secondary" />
 )}
 </button>
 
 {showPaymentSchedule && (
 <div className="space-y-3">
 <p className="text-xs sm:text-sm text-ink-secondary mb-3 sm:mb-4">
 {remainingPayments.length > 0
 ? 'Pay your full remaining balance now, or choose a scheduled installment below.'
 : 'Pay the full remaining balance to complete your booking.'}
 </p>
 
 <button
 type="button"
 onClick={() => {
 setPayAllRemaining(true);
 setSelectedPayment(null);
 }}
 className={`w-full text-left p-3 rounded-md border-2 transition-colors ${ payAllRemaining ? 'border-emerald-600 bg-emerald-100' : 'border-border bg-surface hover:border-emerald-300' }`}
 >
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <div className="flex items-center mb-1">
 <DollarSign className={`w-4 h-4 mr-2 flex-shrink-0 ${payAllRemaining ? 'text-emerald-600' : 'text-gray-400'}`} />
 <span className="font-medium text-sm text-ink">Pay All Remaining Balance</span>
 </div>
 <p className="text-xs text-ink-secondary ml-6">Pay the full remaining balance now</p>
 {(() => {
 const totalDue = parseFloat(booking?.subtotal_amount || 0) + parseFloat(booking?.platform_fee || 0);
 const remainingPercentage = totalDue > 0 ? (balanceAmount / totalDue) * 100 : 0;
 const proportionalFee = (parseFloat(booking?.platform_fee || 0) * remainingPercentage) / 100;
 return (
 <p className="text-xs text-ink-tertiary ml-6 mt-1">
 Includes ${proportionalFee.toFixed(2)} platform fee
 </p>
 );
 })()}
 </div>
 <div className="text-right flex-shrink-0">
 <span className="font-bold text-sm text-ink">
 ${balanceAmount.toFixed(2)}
 </span>
 </div>
 </div>
 </button>
 
 {remainingPayments.length > 0 && (
 <div className="space-y-2">
 {remainingPayments.map((payment) => {
 const isSelected = !payAllRemaining && selectedPayment?.index === payment.index;
 const proportionalFee = (parseFloat(booking?.platform_fee || 0) * payment.percentage) / 100;
 
 return (
 <button
 type="button"
 key={payment.index}
 onClick={() => {
 setSelectedPayment(payment);
 setPayAllRemaining(false);
 }}
 className={`w-full text-left p-3 rounded-md border-2 transition-colors ${ isSelected ? 'border-ink bg-surface-muted' : 'border-border bg-surface hover:border-border' }`}
 >
 <div className="flex items-start justify-between gap-3">
 <div className="flex-1 min-w-0">
 <div className="flex items-center mb-1 flex-wrap">
 <DollarSign className={`w-4 h-4 mr-2 flex-shrink-0 ${isSelected ? 'text-ink' : 'text-gray-400'}`} />
 <span className="font-medium text-sm text-ink">{payment.label}</span>
 <span className="ml-2 text-xs text-ink-tertiary">({payment.percentage}%)</span>
 </div>
 <p className="text-xs text-ink-secondary ml-6 break-words">{payment.dueDate}</p>
 <p className="text-xs text-ink-tertiary ml-6 mt-1">
 Includes ${proportionalFee.toFixed(2)} platform fee
 </p>
 </div>
 <div className="text-right flex-shrink-0">
 <span className="font-bold text-sm text-ink">
 ${payment.remainingAmount.toFixed(2)}
 </span>
 </div>
 </div>
 </button>
 );
 })}
 </div>
 )}
 </div>
 )}
 </div>
 )}

 <StripePaymentForm
 amount={selectedPaymentAmount}
 bookingId={booking.id}
 paymentType={paymentType}
 onSuccess={onPaymentSuccess}
 onError={onPaymentError}
 metadata={paymentMetadata}
 />
 </div>
 )}
 </ModalShell>
 );
};

export default BalancePaymentModal;
